import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';

import {ApiError, CompanyEmployeesService, IndividualSubordinatesService, PermissionModel} from '@spa/api';

import {Peer} from '../../../../shared/classes';
import {FormComponent} from '../../../../shared/widgets';
import {FormInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent} from '../../../../shared/components';

import {EventsService} from '../../services';
import {SupervisedRightsForm} from '../../forms';
import {SupervisedRightsFormErrorStatesEnum} from '../../enums/error-states';


import {
    AssignSupervisedRightsDialogConfig,
    SupervisedRightsNonSupervisedUserSelector
} from '../../../../../assets/descriptors';

import {AssignSupervisedRightsMessages} from '../../../../../assets/messages';

@Component({
    selector: 'app-assign-supervised-rights',
    templateUrl: './assign-supervised-rights.component.html',
    styleUrls: ['./assign-supervised-rights.component.scss']
})
export class AssignSupervisedRightsComponent
    extends DialogComponent<AssignSupervisedRightsComponent, boolean> implements FormInterface, OnDestroy {

    private formSubscription: Subscription;

    public supervisedRightsForm: SupervisedRightsForm;

    constructor(
        private companyEmployeesService: CompanyEmployeesService,
        private individualSubordinatesService: IndividualSubordinatesService,
        private eventsService: EventsService,
        @Inject(MAT_DIALOG_DATA) public data: {
            clientUserId: number,
            clientUserRelationType: number,
            nonSupervisedUsersPeers: Peer[],
            permissions: PermissionModel[]
        },
        public dialogRef: MatDialogRef<AssignSupervisedRightsComponent>
    ) {
        super(dialogRef);

        this.config = AssignSupervisedRightsDialogConfig();

        this.supervisedRightsForm = new SupervisedRightsForm(this.companyEmployeesService, this.individualSubordinatesService);

        this.initForm();
    }

    private getDialogActionMessage(getFailedMessage = false): string {
        const formValues = this.supervisedRightsForm.form.value;

        const selectedPeer = this.data.nonSupervisedUsersPeers.find((peer: Peer) => {
            return peer.key === formValues[SupervisedRightsNonSupervisedUserSelector];
        });

        return (
            !getFailedMessage ? AssignSupervisedRightsMessages.AssignRightsSucceed.message :
                AssignSupervisedRightsMessages.AssignRightsFailed.message
        ).replace('<<name>>', !selectedPeer ? '' : ('<b>' + selectedPeer.value + '</b>'));
    }

    ngOnDestroy(): void {
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
        }
    }

    getFormMessages(controlLabel?: string, controlMaxLength?: number): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel, controlMaxLength);
    }

    initForm(): void {
        this.supervisedRightsForm.initForm(this.data.permissions, this.data.nonSupervisedUsersPeers);
    }

    validateForm(): boolean {
        return this.supervisedRightsForm.validateForm(this.getFormMessages);
    }

    onFormSubmit(action?: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close({action, status: !this.config.process.failed});
        } else {
            if (this.validateForm()) {
                this.startLoading(() => {
                    this.formSubscription = this.supervisedRightsForm
                        .createRequest(this.data.clientUserId, this.data.clientUserRelationType)
                        .subscribe(this.dialogSubscribeHandler.bind(
                            this,
                            () => {
                                this.config.process.succeedMessage = this.getDialogActionMessage();
                                this.eventsService.forceSupervisedRightsGet.next(true);
                            },
                            (errors: ApiError[]) => {
                                this.config.process.failedMessage = errors.map((error: ApiError) => {
                                    if (error.status === SupervisedRightsFormErrorStatesEnum.AssignRightsFailed) {
                                        return this.getDialogActionMessage(true);
                                    }

                                    return AssignSupervisedRightsMessages.UnknownError.message;
                                }).join('</br>');
                            })
                        );
                });
            }
        }
    }
}
